import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faCloudUploadAlt,
  faDownload,
  faExclamationTriangle,
  faEye,
  faFileAlt,
  faHistory,
  faInfoCircle,
  faTimes,
  faTimesCircle,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { uploadCheckStatusBatch } from "../../services/settlementApi";

const STATUS_TEMPLATE_HEADERS = [
  "CV No.",
  "Check Number",
  "Check Date",
  "Check Status",
  "Date Cleared",
  "Remarks",
];

const REPLACEMENT_TEMPLATE_HEADERS = [
  "CV No.",
  "Original Check Number",
  "New Check Number",
  "New Check Date",
  "Check Date",
  "Check Status",
  "Reason for Replacement",
  "Replacement Fee Deduction Amount",
  "Remarks",
];

const STATUS_COLUMNS = [
  { label: "CV No.", required: true, note: "Voucher number from settlement" },
  { label: "Check Number", required: true, note: "Existing check number" },
  { label: "Check Date", required: false, note: "MM/DD/YYYY; optional if settlement check date exists" },
  { label: "Check Status", required: true, note: "For Clearing, Cleared, Outstanding, Stale, Replaced, Cancelled" },
  { label: "Date Cleared", required: false, note: "MM/DD/YYYY; required only when status is Cleared" },
  { label: "Remarks", required: false, note: "Optional notes" },
];

const REPLACEMENT_COLUMNS = [
  { label: "CV No.", required: true, note: "Voucher number from settlement" },
  { label: "Original Check Number", required: true, note: "Existing/original check number" },
  { label: "New Check Number", required: true, note: "Replacement check number" },
  { label: "New Check Date", required: true, note: "MM/DD/YYYY replacement check date" },
  { label: "Check Date", required: false, note: "MM/DD/YYYY optional check date override" },
  { label: "Check Status", required: true, note: "Usually Replaced" },
  { label: "Reason for Replacement", required: true, note: "Must match one of the approved reasons" },
  { label: "Replacement Fee Deduction Amount", required: false, note: "Used for Staled or Ripped Check only" },
  { label: "Remarks", required: false, note: "Optional notes" },
];

const downloadCsvTemplate = (headers, filename, sampleRows = []) => {
  const rows = [headers, ...sampleRows];
  const csv = rows
    .map((row) =>
      row
        .map((value) => {
          const text = String(value ?? "");
          return `"${text.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  URL.revokeObjectURL(url);
};

const CheckStatusBatchUploadPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const isValidType =
      selectedFile.type === "text/csv" ||
      selectedFile.type === "text/plain" ||
      selectedFile.name.toLowerCase().endsWith(".csv") ||
      selectedFile.name.toLowerCase().endsWith(".txt");

    if (!isValidType) {
      toast.error("Please select a valid CSV file.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10 MB limit.");
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.warn("Please select a CSV file first.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadCheckStatusBatch(formData);
      const result = response.data;

      setUploadResult({
        success: true,
        queued: true,
        uploadId: result.upload_id,
        fileName: result.file_name,
        fileSize: result.file_size,
        message: result.message,
      });

      toast.success("Check Status batch uploaded. Queue processing started.");

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      const message = error.response?.data?.message || error.message || "Upload failed.";
      setUploadResult({ success: false, message });
      toast.error(typeof message === "string" ? message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect({ target: { files: e.dataTransfer.files } });
    }
  };

  const renderColumnList = (columns) => (
    <div className="space-y-2">
      {columns.map((column) => (
        <div
          key={column.label}
          className={`flex items-start gap-2 p-2.5 rounded border ${
            column.required ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
          }`}
        >
          <span className={`mt-1.5 w-2 h-2 rounded-full ${column.required ? "bg-red-500" : "bg-gray-400"}`} />
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${column.required ? "text-red-700" : "text-gray-700"}`}>
              {column.label}
              {column.required && <span className="ml-1 text-xs uppercase text-red-600">required</span>}
            </p>
            <p className="text-xs text-gray-500">{column.note}</p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Batch Check Status Upload</h1>
          <p className="text-gray-600">Upload Check Status CSV rows with saved history and processed row tracking.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate("/check-status-batch-upload-history")}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faHistory} />
            History
          </button>
          <button
            onClick={() => navigate("/settlement-list")}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Settlement List
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-2 space-y-5">
          <div className="bg-white rounded-lg shadow-md p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
                Status Tagging CSV
              </h2>
              <button
                onClick={() => {
                  downloadCsvTemplate(STATUS_TEMPLATE_HEADERS, "check_status_tagging_template.csv", [
                    ["1000000001", "CHK000001", "06/01/2026", "For Clearing", "", "Sample status tagging row"],
                    ["1000000001", "CHK000002", "06/02/2026", "Cleared", "06/05/2026", "Sample cleared row"],
                  ]);
                  toast.success("Status template downloaded.");
                }}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} />
                Template
              </button>
            </div>
            {renderColumnList(STATUS_COLUMNS)}
          </div>

          <div className="bg-white rounded-lg shadow-md p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-purple-600" />
                Replacement CSV
              </h2>
              <button
                onClick={() => {
                  downloadCsvTemplate(REPLACEMENT_TEMPLATE_HEADERS, "replacement_check_tagging_template.csv", [
                    ["1000000001", "CHK000001", "CHK000002", "06/02/2026", "06/02/2026", "Replaced", "Staled", "0.00", "Waived replacement fee sample"],
                    ["1000000001", "CHK000002", "CHK000003", "06/03/2026", "06/03/2026", "Replaced", "Ripped Check", "150.00", "Sample charged replacement fee"],
                  ]);
                  toast.success("Replacement template downloaded.");
                }}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} />
                Template
              </button>
            </div>
            {renderColumnList(REPLACEMENT_COLUMNS)}
          </div>
        </div>

        <div className="xl:col-span-3">
          <div className="bg-white rounded-lg shadow-md p-6 mb-5">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="text-blue-600" />
              Upload CSV File
            </h2>

            <div
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                isDragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className={`text-5xl mb-3 ${isDragOver ? "text-blue-600" : "text-blue-400"}`}
              />
              <h3 className="text-base font-semibold text-gray-700 mb-1">Drag and drop your CSV file here</h3>
              <p className="text-sm text-gray-500 mb-3">or click to browse</p>
              <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFileSelect} className="hidden" />
              <p className="text-xs text-gray-400">Accepted: .csv or .txt - Max size: 10 MB</p>
            </div>

            {file && (
              <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="text-gray-400 hover:text-red-500"
                  title="Remove file"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2.5 px-4 rounded flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faUpload} className={uploading ? "animate-spin" : ""} />
                {uploading ? "Uploading..." : "Upload and Process"}
              </button>
              <button
                onClick={() => {
                  setFile(null);
                  setUploadResult(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                disabled={uploading}
                className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 font-semibold rounded flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faTimes} />
                Clear
              </button>
            </div>

            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-xs font-bold text-yellow-800 mb-1.5 flex items-center gap-1">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Notes
              </p>
              <ul className="text-xs text-yellow-800 space-y-1 list-disc list-inside">
                <li>CSV date values must use MM/DD/YYYY.</li>
                <li>Replacement rows update sr.check_no after the monitoring row is saved.</li>
                <li>All uploaded files, successes, and failed rows are saved in history.</li>
              </ul>
            </div>
          </div>

          {uploadResult && (
            <div className={`rounded-lg p-5 ${uploadResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={uploadResult.success ? faCheckCircle : faTimesCircle}
                  className={`text-2xl flex-shrink-0 mt-0.5 ${uploadResult.success ? "text-green-600" : "text-red-600"}`}
                />
                <div className="flex-1">
                  <h3 className={`text-base font-bold mb-1 ${uploadResult.success ? "text-green-800" : "text-red-800"}`}>
                    {uploadResult.success ? "Upload Queued" : "Upload Failed"}
                  </h3>
                  <p className={`text-sm ${uploadResult.success ? "text-green-700" : "text-red-700"}`}>
                    {typeof uploadResult.message === "string" ? uploadResult.message : "An error occurred."}
                  </p>

                  {uploadResult.success && (
                    <>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-3 bg-white rounded border border-green-200">
                          <p className="text-xs text-gray-500">Queue Status</p>
                          <p className="text-lg font-bold text-blue-700">Processing started</p>
                        </div>
                        <div className="p-3 bg-white rounded border border-green-200">
                          <p className="text-xs text-gray-500">Upload ID</p>
                          <p className="font-mono text-xs text-gray-800 break-all">{uploadResult.uploadId}</p>
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        Processing runs in the background. Check History for status and Processed rows for row-level success/error details.
                      </p>

                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => navigate(`/check-status-batch-upload-processed/${uploadResult.uploadId}`)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Processed
                        </button>
                        <button
                          onClick={() => navigate("/check-status-batch-upload-history")}
                          className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2.5 px-4 rounded flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                          View History
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CheckStatusBatchUploadPage;
